import { useQuery } from '@tanstack/react-query';
import { LOCATION_TYPES, type LocationDto, type LpnDto } from '@zenarty/shared';
import { Download, Grid3x3, MapPin, Plus, Power, Tag, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api, downloadFile } from '../api';
import { useAuth } from '../auth';
import { EntityHistory } from '../components/audit';
import { ImportDialog } from '../components/importer';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  KeyValue,
  PageHeader,
  Stat,
  StatusBadge,
  Tabs,
  errorText,
} from '../components/kit';
import { ConfirmDialog, Dialog, Drawer } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { useToast } from '../components/toast';
import { formatDateTime, formatNumber, timeAgo, titleCase } from '../lib/format';
import { nameMap, useAction, useClients, useServerList, useSites } from '../lib/queries';

interface Summary {
  total: number;
  pickable: number;
  byZone: { zone: string; total: number; temperatureZone: string }[];
  byType: { type: string; total: number }[];
}

const TEMP_TONE = { ambient: 'neutral', chilled: 'info', frozen: 'accent' } as const;

export function LocationsPage() {
  const sites = useSites();
  const [siteId, setSiteId] = useState('');
  const [tab, setTab] = useState<'locations' | 'lpns'>('locations');
  useEffect(() => {
    const first = sites.data?.items.find((s) => s.status === 'active');
    if (!siteId && first) setSiteId(first.id);
  }, [sites.data, siteId]);
  const site = sites.data?.items.find((s) => s.id === siteId);

  return (
    <>
      <PageHeader
        title="Locations"
        description="Where stock lives: the zone, aisle, bay and level layout of each site, its capacity and handling rules, and the license plates (LPNs) that move through it."
        actions={
          <select
            className="select"
            aria-label="Site"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            style={{ minWidth: 220 }}
          >
            {(sites.data?.items ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'locations', label: 'Locations' },
          { id: 'lpns', label: 'License plates' },
        ]}
      />
      {siteId && tab === 'locations' ? (
        <LocationsTab siteId={siteId} siteCode={site?.code ?? ''} />
      ) : null}
      {siteId && tab === 'lpns' ? <LpnsTab siteId={siteId} /> : null}
      {sites.data && !sites.data.items.length ? (
        <EmptyState icon={MapPin} title="Add a site first" />
      ) : null}
    </>
  );
}

function LocationsTab({ siteId, siteCode }: { siteId: string; siteCode: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const clients = useClients(can('client:read'));
  const clientName = nameMap(clients.data?.items, (c) => c.name);
  const [zone, setZone] = useState('');
  const [type, setType] = useState('');
  const [dialog, setDialog] = useState<'generate' | 'import' | 'new' | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  useEffect(() => setZone(''), [siteId]);
  const summary = useQuery({
    queryKey: ['locations', 'summary', siteId],
    queryFn: () => api<Summary>(`/locations/summary?siteId=${siteId}`),
  });
  const { query, server } = useServerList<LocationDto>('locations', '/locations', {
    siteId,
    zone,
    type,
  });
  const s = summary.data;

  const columns: Column<LocationDto>[] = [
    {
      id: 'code',
      header: 'Location',
      cell: (l) => <span className="primary-cell mono">{l.code}</span>,
    },
    { id: 'zone', header: 'Zone', cell: (l) => <span className="tag">{l.zone}</span> },
    { id: 'type', header: 'Type', cell: (l) => titleCase(l.type) },
    {
      id: 'temp',
      header: 'Temperature',
      cell: (l) => (
        <Badge tone={TEMP_TONE[l.temperatureZone]}>{titleCase(l.temperatureZone)}</Badge>
      ),
    },
    {
      id: 'pick',
      header: 'Pick face',
      cell: (l) =>
        l.pickable ? <Badge tone="ok">Pickable</Badge> : <span className="faint">Reserve</span>,
    },
    {
      id: 'cap',
      header: 'Capacity',
      cell: (l) => (
        <span className="muted nowrap">
          {l.maxPallets ? `${l.maxPallets} pallet${l.maxPallets > 1 ? 's' : ''}` : '—'}
          {l.maxWeightG ? ` · ${formatNumber(Math.round(l.maxWeightG / 453.6))} lb` : ''}
        </span>
      ),
    },
    {
      id: 'rules',
      header: 'Rules',
      cell: (l) => (
        <div className="row" style={{ gap: 4 }}>
          {l.dedicatedClientId ? (
            <Badge tone="accent">{clientName.get(l.dedicatedClientId) ?? 'Dedicated'}</Badge>
          ) : null}
          {l.hazmatZone ? <Badge tone="danger">Hazmat</Badge> : null}
          {!l.allowMixedSku ? <Badge>Single SKU</Badge> : null}
        </div>
      ),
    },
    {
      id: 'seq',
      header: 'Pick seq.',
      align: 'right',
      cell: (l) => l.pickSequence.toLocaleString(),
    },
    { id: 'status', header: 'Status', cell: (l) => <StatusBadge status={l.status} /> },
  ];

  const open = query.data?.items.find((l) => l.id === openId) ?? null;
  return (
    <div className="stack">
      <div className="grid-stats">
        <Stat
          label="Locations"
          icon={MapPin}
          value={s ? formatNumber(s.total) : '—'}
          foot={s ? `${s.byZone.length} zones` : null}
        />
        <Stat
          label="Pick faces"
          icon={Grid3x3}
          tone="ok"
          value={s ? formatNumber(s.pickable) : '—'}
          foot={s ? `${formatNumber(s.total - s.pickable)} reserve / other` : null}
        />
        <Card title="By type" className="stat">
          <div className="row" style={{ gap: 6 }}>
            {(s?.byType ?? []).map((t) => (
              <span key={t.type} className="tag">
                {titleCase(t.type)} {t.total}
              </span>
            ))}
          </div>
        </Card>
      </div>
      {s && s.byZone.length ? (
        <div className="row">
          <span className="small muted">Zones:</span>
          <button
            type="button"
            className={`tag ${!zone ? 'badge-accent' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setZone('')}
          >
            All
          </button>
          {s.byZone.map((z) => (
            <button
              key={`${z.zone}-${z.temperatureZone}`}
              type="button"
              className="tag"
              style={{
                cursor: 'pointer',
                ...(zone === z.zone
                  ? { background: 'var(--accent-soft)', color: 'var(--accent)' }
                  : {}),
              }}
              onClick={() => setZone(z.zone)}
            >
              {z.zone} · {z.total}
              {z.temperatureZone !== 'ambient' ? ` · ${z.temperatureZone}` : ''}
            </button>
          ))}
        </div>
      ) : null}
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(l) => l.id}
        loading={query.isLoading}
        server={server}
        searchPlaceholder="Location code starts with…"
        onRowClick={(l) => setOpenId(l.id)}
        toolbar={
          <>
            <select
              className="select"
              aria-label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">All types</option>
              {LOCATION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {titleCase(t)}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="ghost"
              icon={Download}
              onClick={() =>
                void downloadFile(
                  `/locations/export?siteId=${siteId}`,
                  `locations-${siteCode}.csv`,
                ).catch((e: unknown) => toast.error(errorText(e)))
              }
            >
              Export
            </Button>
            {can('location:manage') ? (
              <>
                <Button size="sm" variant="ghost" icon={Upload} onClick={() => setDialog('import')}>
                  Import
                </Button>
                <Button size="sm" icon={Grid3x3} onClick={() => setDialog('generate')}>
                  Generate
                </Button>
              </>
            ) : null}
          </>
        }
        empty={
          <EmptyState
            icon={MapPin}
            title="No locations here yet"
            action={
              can('location:manage') ? (
                <Button variant="primary" icon={Grid3x3} onClick={() => setDialog('generate')}>
                  Generate a rack layout
                </Button>
              ) : null
            }
          >
            Generate aisles, bays and levels in one step, or import your existing layout from CSV.
          </EmptyState>
        }
      />
      {dialog === 'generate' ? (
        <GenerateDialog siteId={siteId} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'import' ? (
        <ImportDialog
          title={`Import locations into ${siteCode}`}
          description="New codes are created and existing codes updated. Nothing is written if any row has a problem."
          endpoint="/locations/import"
          scope={{ siteId }}
          scopeReady
          invalidate="locations"
          templateName="locations-template.csv"
          templateHeaders={[
            'code',
            'zone',
            'aisle',
            'bay',
            'level',
            'position',
            'type',
            'temperature_zone',
            'hazmat_zone',
            'pickable',
            'max_pallets',
            'max_weight_g',
            'allow_mixed_sku',
            'dedicated_client_code',
            'pick_sequence',
          ]}
          templateExample={[
            'A-01-01-A',
            'A',
            '01',
            '01',
            'A',
            '',
            'rack',
            'ambient',
            'N',
            'Y',
            1,
            1000000,
            'N',
            '',
            10,
          ]}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {open ? (
        <LocationDrawer
          location={open}
          clientName={open.dedicatedClientId ? clientName.get(open.dedicatedClientId) : undefined}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </div>
  );
}

function LocationDrawer({
  location: l,
  clientName,
  onClose,
}: {
  location: LocationDto;
  clientName?: string;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const patch = useAction(
    (body: Record<string, unknown>) => api(`/locations/${l.id}`, { method: 'PATCH', body }),
    {
      invalidate: [['locations']],
      success: 'Location updated',
      onSuccess: () => setConfirm(false),
    },
  );
  return (
    <Drawer
      title={<span className="mono">{l.code}</span>}
      subtitle={`${titleCase(l.type)} · zone ${l.zone}`}
      icon={
        <span className="icon-tile accent">
          <MapPin />
        </span>
      }
      onClose={onClose}
      footer={
        can('location:manage') ? (
          <>
            <Button onClick={() => patch.mutate({ pickable: !l.pickable })}>
              {l.pickable ? 'Make reserve' : 'Make pick face'}
            </Button>
            <Button
              variant={l.status === 'active' ? 'danger' : 'default'}
              icon={Power}
              onClick={() => setConfirm(true)}
            >
              {l.status === 'active' ? 'Block location' : 'Unblock'}
            </Button>
          </>
        ) : null
      }
    >
      <section>
        <div className="section-title">Address</div>
        <KeyValue
          items={[
            ['Building', l.building],
            ['Zone', l.zone],
            ['Aisle', l.aisle],
            ['Bay', l.bay],
            ['Level', l.level],
            ['Position', l.position],
            ['Coordinates', l.x !== null ? `x ${l.x}, y ${l.y}` : null],
            ['Pick sequence', l.pickSequence.toLocaleString()],
          ]}
        />
      </section>
      <section>
        <div className="section-title">Rules and capacity</div>
        <KeyValue
          items={[
            ['Status', <StatusBadge key="s" status={l.status} />],
            ['Temperature', titleCase(l.temperatureZone)],
            ['Hazmat zone', l.hazmatZone ? 'Yes' : 'No'],
            ['Dedicated to', clientName ?? 'Shared'],
            [
              'Mixing',
              [
                l.allowMixedSku ? 'SKUs' : null,
                l.allowMixedLot ? 'lots' : null,
                l.allowMixedClient ? 'clients' : null,
              ]
                .filter(Boolean)
                .join(', ') || 'None',
            ],
            ['Max pallets', l.maxPallets],
            [
              'Max weight',
              l.maxWeightG ? `${formatNumber(Math.round(l.maxWeightG / 453.6))} lb` : null,
            ],
            ['Created', formatDateTime(l.createdAt)],
          ]}
        />
      </section>
      {can('audit:read') ? (
        <section>
          <div className="section-title">History</div>
          <EntityHistory entityId={l.id} />
        </section>
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={l.status === 'active' ? `Block ${l.code}?` : `Unblock ${l.code}?`}
          confirmLabel={l.status === 'active' ? 'Block' : 'Unblock'}
          danger={l.status === 'active'}
          pending={patch.isPending}
          onConfirm={() => patch.mutate({ status: l.status === 'active' ? 'inactive' : 'active' })}
          onClose={() => setConfirm(false)}
        >
          {l.status === 'active'
            ? 'No putaway or picking will be directed here until it is unblocked (e.g. damaged racking).'
            : 'The location becomes available for putaway and picking again.'}
        </ConfirmDialog>
      ) : null}
    </Drawer>
  );
}

function GenerateDialog({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const [f, setF] = useState({
    zone: 'A',
    aFrom: '1',
    aTo: '4',
    bFrom: '1',
    bTo: '10',
    levels: 'A,B,C,D',
    pickLevels: 'A,B',
    type: 'rack',
    temperatureZone: 'ambient',
    maxPallets: '1',
    hazmat: false,
  });
  const set = (k: keyof typeof f, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const levels = f.levels
    .split(',')
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean);
  const count =
    Math.max(0, Number(f.aTo) - Number(f.aFrom) + 1) *
    Math.max(0, Number(f.bTo) - Number(f.bFrom) + 1) *
    levels.length;
  const sample = useMemo(() => {
    const z = f.zone.toUpperCase();
    const a = String(Number(f.aFrom)).padStart(2, '0');
    const b = String(Number(f.bFrom)).padStart(2, '0');
    return levels.length ? `${z}-${a}-${b}-${levels[0]}` : '';
  }, [f.zone, f.aFrom, f.bFrom, levels]);
  const gen = useAction(
    () =>
      api<{ created: number; skipped: number }>('/locations/generate', {
        method: 'POST',
        body: {
          siteId,
          zone: f.zone,
          aisles: { from: Number(f.aFrom), to: Number(f.aTo) },
          bays: { from: Number(f.bFrom), to: Number(f.bTo) },
          levels,
          type: f.type,
          temperatureZone: f.temperatureZone,
          hazmatZone: f.hazmat,
          pickableLevels: f.pickLevels
            .split(',')
            .map((x) => x.trim().toUpperCase())
            .filter(Boolean),
          maxPallets: f.maxPallets ? Number(f.maxPallets) : null,
        },
      }),
    {
      invalidate: [['locations']],
      success: (r) =>
        `${r.created.toLocaleString()} locations created${r.skipped ? `, ${r.skipped} already existed` : ''}`,
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title="Generate locations"
      description="Creates every combination of aisle, bay and level, numbered in a serpentine pick path (up one aisle, down the next). Existing codes are skipped."
      onClose={onClose}
      wide
      footer={
        <>
          <span className="muted small" style={{ marginRight: 'auto' }}>
            {count.toLocaleString()} locations · e.g. <code>{sample}</code>
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={gen.isPending}
            disabled={count < 1 || count > 5000}
            onClick={() => gen.mutate(undefined)}
          >
            Generate {count.toLocaleString()}
          </Button>
        </>
      }
    >
      <div className="form-grid-3">
        <Field label="Zone">
          <input
            className="input mono"
            value={f.zone}
            onChange={(e) => set('zone', e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Type">
          <select className="select" value={f.type} onChange={(e) => set('type', e.target.value)}>
            {LOCATION_TYPES.map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Temperature">
          <select
            className="select"
            value={f.temperatureZone}
            onChange={(e) => set('temperatureZone', e.target.value)}
          >
            <option value="ambient">Ambient</option>
            <option value="chilled">Chilled</option>
            <option value="frozen">Frozen</option>
          </select>
        </Field>
        <Field label="Aisles from / to">
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <input
              className="input"
              value={f.aFrom}
              onChange={(e) => set('aFrom', e.target.value)}
            />
            <input className="input" value={f.aTo} onChange={(e) => set('aTo', e.target.value)} />
          </div>
        </Field>
        <Field label="Bays from / to">
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <input
              className="input"
              value={f.bFrom}
              onChange={(e) => set('bFrom', e.target.value)}
            />
            <input className="input" value={f.bTo} onChange={(e) => set('bTo', e.target.value)} />
          </div>
        </Field>
        <Field label="Levels" hint="Comma-separated, bottom first">
          <input
            className="input mono"
            value={f.levels}
            onChange={(e) => set('levels', e.target.value)}
          />
        </Field>
        <Field label="Pickable levels" hint="Others are reserve">
          <input
            className="input mono"
            value={f.pickLevels}
            onChange={(e) => set('pickLevels', e.target.value)}
          />
        </Field>
        <Field label="Pallets per location">
          <input
            className="input"
            value={f.maxPallets}
            onChange={(e) => set('maxPallets', e.target.value)}
          />
        </Field>
        <div className="field">
          <span className="field-label">Hazmat</span>
          <label className="check">
            <input
              type="checkbox"
              checked={f.hazmat}
              onChange={(e) => set('hazmat', e.target.checked)}
            />{' '}
            Hazmat-approved zone
          </label>
        </div>
      </div>
      {count > 5000 ? <p className="danger-text small">At most 5,000 locations per run.</p> : null}
    </Dialog>
  );
}

function LpnsTab({ siteId }: { siteId: string }) {
  const { can } = useAuth();
  const [type, setType] = useState('');
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const { query, server } = useServerList<LpnDto>('lpns', '/lpns', { siteId, type });
  const columns: Column<LpnDto>[] = [
    { id: 'code', header: 'LPN', cell: (l) => <span className="primary-cell mono">{l.code}</span> },
    { id: 'type', header: 'Type', cell: (l) => titleCase(l.type) },
    {
      id: 'loc',
      header: 'Location',
      cell: (l) =>
        l.locationCode ? (
          <span className="mono">{l.locationCode}</span>
        ) : (
          <span className="faint">—</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (l) => (
        <Badge tone={l.status === 'in_use' ? 'ok' : l.status === 'empty' ? 'neutral' : 'info'} dot>
          {titleCase(l.status)}
        </Badge>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      cell: (l) => <span className="muted">{timeAgo(l.createdAt)}</span>,
    },
  ];
  return (
    <>
      {codes ? (
        <Card
          title={`${codes.length} LPNs created`}
          sub="Send these to the label printer (ZPL printing arrives with labels in Milestone 6)"
          actions={
            <Button size="sm" variant="ghost" onClick={() => setCodes(null)}>
              Dismiss
            </Button>
          }
        >
          <div className="row mono small">
            {codes.slice(0, 60).map((c) => (
              <span key={c} className="code-pill">
                {c}
              </span>
            ))}
            {codes.length > 60 ? <span className="muted">…</span> : null}
          </div>
        </Card>
      ) : null}
      <div style={{ height: codes ? 16 : 0 }} />
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(l) => l.id}
        loading={query.isLoading}
        server={server}
        searchPlaceholder="Search LPN"
        toolbar={
          <>
            <select
              className="select"
              aria-label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">All types</option>
              <option value="pallet">Pallets</option>
              <option value="case">Cases</option>
              <option value="tote">Totes</option>
            </select>
            {can('location:manage') ? (
              <Button size="sm" icon={Plus} onClick={() => setOpen(true)}>
                Generate LPNs
              </Button>
            ) : null}
          </>
        }
        empty={
          <EmptyState icon={Tag} title="No license plates yet">
            LPNs identify every pallet, case and tote. Generate a batch to pre-print labels.
          </EmptyState>
        }
      />
      {open ? (
        <GenerateLpns
          siteId={siteId}
          onDone={(c) => {
            setCodes(c);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function GenerateLpns({
  siteId,
  onDone,
  onClose,
}: {
  siteId: string;
  onDone: (codes: string[]) => void;
  onClose: () => void;
}) {
  const [type, setType] = useState('pallet');
  const [count, setCount] = useState('50');
  const gen = useAction(
    () =>
      api<{ codes: string[] }>('/lpns/generate', {
        method: 'POST',
        body: { siteId, type, count: Number(count) },
      }),
    {
      invalidate: [['lpns']],
      success: (r) => `${r.codes.length} LPNs created`,
      onSuccess: (r) => onDone(r.codes),
    },
  );
  return (
    <Dialog
      title="Generate LPNs"
      description="Numbers come from a single sequence, so they are never reused."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={gen.isPending} onClick={() => gen.mutate(undefined)}>
            Generate
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Container type">
          <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="pallet">Pallet</option>
            <option value="case">Case</option>
            <option value="tote">Tote</option>
          </select>
        </Field>
        <Field label="How many" hint="1–500">
          <input className="input" value={count} onChange={(e) => setCount(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
